import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { getTheme } from '../../utils/cards';
import { prepareLogo, removeStoredImage, uploadImage } from '../../utils/storage';
import { logoHasOpaqueBackground } from '../../utils/nfcPrint';
import { DEFAULT_THEME, normalizeLayout, resolveTheme } from '../../utils/theme';
import { errorBox, noticeBox, primaryButton } from '../AuthLayout';
import { ColorField, ImagePicker, LayoutPicker, Section, Toggle } from '../EditorParts';
import CardDisplay from '../CardDisplay';
import PhoneFrame from '../PhoneFrame';

/** Card fields a company can lock, in checklist order (values are brand_themes.locked_fields entries). */
export const LOCKABLE = [
  { id: 'company', label: 'Company name' },
  { id: 'department', label: 'Department' },
  { id: 'website', label: 'Website' },
  { id: 'phone', label: 'Phone' },
  { id: 'email', label: 'Email' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'photo_url', label: 'Profile photo' },
  { id: 'layout', label: 'Layout' },
];

/** Company → Brand: logo, colors, default layout, lead capture and locked fields for every card. */
export default function Brand({ org, userId }) {
  const [theme, setTheme] = useState(undefined); // undefined = loading, null = none yet
  const [design, setDesign] = useState(DEFAULT_THEME);
  const [logoFile, setLogoFile] = useState(null);
  const [logoBackdrop, setLogoBackdrop] = useState(false); // chosen raster logo has its own opaque background
  const [sample, setSample] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [t, mine] = await Promise.all([
          getTheme(org.id),
          supabase.from('cards').select('*').eq('org_id', org.id).eq('user_id', userId).maybeSingle(),
        ]);
        if (!active) return;
        setTheme(t);
        setDesign({ ...DEFAULT_THEME, ...(t || {}), layout: normalizeLayout(t?.layout), locked_fields: t?.locked_fields || [] });
        setSample(mine.data || null);
      } catch (e) {
        if (active) setError(e.message || 'Could not load the company design.');
      }
    })();
    return () => { active = false; };
  }, [org.id, userId]);

  useEffect(() => {
    let active = true;
    setLogoBackdrop(false);
    if (logoFile) logoHasOpaqueBackground(logoFile).then((v) => active && setLogoBackdrop(v));
    return () => { active = false; };
  }, [logoFile]);

  const logoPreview = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : design.logo_url || null), [logoFile, design.logo_url]);
  const set = (patch) => { setDesign((d) => ({ ...d, ...patch })); setSaved(false); };
  const locked = design.locked_fields || [];
  const toggleLock = (id) => set({ locked_fields: locked.includes(id) ? locked.filter((f) => f !== id) : [...locked, id] });

  const save = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      let logo_url = design.logo_url || null;
      if (logoFile) logo_url = await uploadImage('brand-logos', org.id, await prepareLogo(logoFile));
      const row = {
        logo_url,
        primary_color: design.primary_color,
        accent_color: design.accent_color,
        layout: design.layout,
        force_lead_capture: !!design.force_lead_capture,
        locked_fields: locked,
      };
      const { data, error: err } = theme
        ? await supabase.from('brand_themes').update(row).eq('org_id', org.id).select().single()
        : await supabase.from('brand_themes').insert({ ...row, org_id: org.id }).select().single();
      if (err) throw err;
      if (theme?.logo_url && theme.logo_url !== logo_url) await removeStoredImage('brand-logos', theme.logo_url);
      setTheme(data);
      setLogoFile(null);
      setDesign((d) => ({ ...d, logo_url }));
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save the design. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (theme === undefined && !error) return <p className="text-ink-muted">Loading company design...</p>;

  const t = resolveTheme({ ...design, logo_url: logoPreview });
  const previewCard = {
    full_name: 'Alex Rivera', title: 'Sales Manager', department: 'Sales', email: 'alex@example.com', phone: '+1 305 555 0100',
    website: 'https://example.com', ...(sample || {}), company: (sample?.company || org.name),
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8 items-start">
      <form onSubmit={save} className="space-y-5 min-w-0">
        <Section title="Logo and colors" hint="Applies to every card in the company.">
          <div className="space-y-5">
            <div>
              <span className="block text-sm font-semibold text-ink mb-2">Logo</span>
              <ImagePicker preview={logoPreview} onPick={(f) => { setLogoFile(f); setSaved(false); }} disabled={saving}
                label={logoPreview ? 'Change logo' : 'Upload logo'} allowSvg help="SVG, PNG, JPG or WebP. SVGs are cleaned before upload."
                onRemove={() => { setLogoFile(null); set({ logo_url: null }); }} />
              <p className="text-sm text-ink-muted mt-2">Use a transparent PNG or SVG. It's required for printed NFC cards.</p>
              {logoBackdrop && (
                <p className={`${errorBox} mt-2`} role="status" data-testid="brand-logo-backdrop">
                  This logo has its own background, so it would print as a rectangle on NFC cards. Upload a transparent PNG or SVG instead.
                </p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <ColorField label="Brand color" value={design.primary_color} onChange={(v) => set({ primary_color: v })} />
              <ColorField label="Button color" value={design.accent_color} onChange={(v) => set({ accent_color: v })} />
            </div>
          </div>
        </Section>

        <Section title="Default layout"
          hint={locked.includes('layout') ? 'Every card uses this layout (employees can\'t change it).' : 'New cards start with this layout. Employees can pick another unless you lock Layout below.'}>
          <LayoutPicker value={normalizeLayout(design.layout)} onChange={(l) => set({ layout: l })} disabled={saving} t={t} />
        </Section>

        <Section title="Lead capture" hint="When someone taps Save contact, they're invited to share their details first. They can always download the contact without it.">
          <Toggle checked={!!design.force_lead_capture} disabled={saving} onChange={(v) => set({ force_lead_capture: v })}
            label="Turn this on for every card in the company" />
          {!design.force_lead_capture && <p className="text-sm text-ink-muted mt-2">Off: each person decides for their own card.</p>}
        </Section>

        <Section title="Locked fields" hint="Checked fields are set by the company. Employees can't change them on their card; owners and admins still can.">
          <div className="grid sm:grid-cols-2 gap-2.5">
            {LOCKABLE.map((f) => {
              const on = locked.includes(f.id);
              return (
                <label key={f.id} className={`flex items-center gap-3 h-11 px-3 rounded-lg border cursor-pointer transition ${on ? 'border-blue bg-blue-soft' : 'border-line bg-white hover:border-line-strong'}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleLock(f.id)} disabled={saving} className="w-4 h-4 accent-[#265EE6]" />
                  <span className="text-sm font-semibold text-ink flex-1">{f.label}</span>
                  {on && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#265EE6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                  )}
                </label>
              );
            })}
          </div>
          <p className="text-sm text-ink-muted mt-3">Tip: fill in locked fields on each card first (People → Edit), since employees won't be able to.</p>
        </Section>

        {error && <div className={errorBox}>{error}</div>}
        {saved && <div className={noticeBox}>Saved. Every {org.name} card now uses this design.</div>}
        <button type="submit" disabled={saving} className={`${primaryButton.replace('w-full ', 'w-full sm:w-auto ')} px-6`}>
          {saving ? 'Saving...' : 'Save company design'}
        </button>
      </form>

      <aside className="lg:sticky lg:top-6 min-w-0" aria-label="Live preview">
        <p className="text-xs font-semibold tracking-wider uppercase text-ink-muted mb-3">
          Live preview{sample ? ' · your card' : ' · sample card'}
        </p>
        <PhoneFrame>
          <CardDisplay card={previewCard} theme={{ ...design, logo_url: logoPreview }} layout={normalizeLayout(design.layout)}
            className="min-h-[640px] [&>div]:min-h-[640px]" />
        </PhoneFrame>
      </aside>
    </div>
  );
}
